import React, { useRef, useState, useMemo, useEffect } from "react";
import { CollectionType, PriceType, NftDataType } from "../../lib/types";
import { TraitCard } from "./TraitCard";
import { motion } from "framer-motion";
import { ChevronDownIcon, ChevronRightIcon } from "@radix-ui/react-icons";

interface TraitSelectionPanelProps {
  selectedTraitType: string;
  prices: Array<PriceType>;
  getPrices: () => void;
  selectedCollection: CollectionType | undefined;
  selectedPurchase: PriceType | undefined;
  setSelectedPurchase: (purchase: PriceType) => void;
  buttons: Array<{ label: string; color: string; onClick: () => void }>;
  disabled: boolean;
  AllFilteredNFTs: NftDataType[];
  nftLoading: boolean;
  onSelectBooby: (index: number) => void;
  fetchWalletNfts: () => void;
}

const TraitSelectionPanel: React.FC<TraitSelectionPanelProps> = ({
  selectedTraitType,
  prices,
  selectedCollection,
  selectedPurchase,
  setSelectedPurchase,
  buttons,
  disabled,
  AllFilteredNFTs,
  nftLoading,
  onSelectBooby,
  getPrices,
  fetchWalletNfts,
}) => {
  // State declarations
  const [selectedBooby, setSelectedBooby] = useState<Record<number, string>>({});
  const [expandedTypes, setExpandedTypes] = useState<Record<string, boolean>>({});
  const coinRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Filter prices based on selected trait type
  const filteredPrices = useMemo(() => {
    if (!selectedTraitType || selectedTraitType === "Select a Trait Type") {
      return prices;
    }

    return prices.filter(price =>
      price.trait?.trait_type === selectedTraitType
    );
  }, [prices, selectedTraitType]);

  // Group traits by type for better organization
  const groupedTraits = useMemo(() => {
    return filteredPrices.reduce((acc, trait) => {
      const type = trait.trait?.trait_type || "Other";
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(trait);
      return acc;
    }, {} as Record<string, PriceType[]>);
  }, [filteredPrices]);

  // Initialize expanded state for all trait types
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!initializedRef.current && Object.keys(groupedTraits).length > 0) {
      const initialExpandedState = Object.keys(groupedTraits).reduce((acc, type) => {
        acc[type] = true;
        return acc;
      }, {} as Record<string, boolean>);

      setExpandedTypes(initialExpandedState);
      initializedRef.current = true;
    }
  }, [groupedTraits]);

  const handlePurchase = (index: number) => {
    // This is now just a callback to notify the parent of the purchase
    // The animation is handled within the TraitCard component
  };

  const handleSelectBooby = (index: number) => {
    setSelectedBooby((prev) => ({
      ...prev,
      [index]: prev[index] ? "" : `Boobies #${index + 1}`,
    }));
  };

  const toggleTraitType = (type: string) => {
    setExpandedTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  return (
    <div className="h-full w-full overflow-x-hidden">
      <div className="h-full flex flex-col gap-4">
        {/* Header */}
        <div className="sticky top-0 z-20 p-4 glass-panel rounded-xl border border-white/10 shadow-lg backdrop-blur-xl">
          <div className="flex flex-col gap-2 items-center">
            <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand-cyan to-brand-purple tracking-tight">
              {selectedTraitType === "Select a Trait Type"
                ? "Plume Protocol"
                : `${selectedTraitType} Collection`}
            </h2>
            <p className="text-gray-400 text-center max-w-lg text-sm">
              {selectedTraitType === "Select a Trait Type"
                ? "Explore all available traits to customize your Boobie"
                : `Discover unique ${selectedTraitType.toLowerCase()} traits for your Boobie`}
            </p>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto scrollbar-hide">
          {Object.keys(groupedTraits).length > 0 ? (
            <div className="flex flex-col gap-6 p-2">
              {Object.entries(groupedTraits).map(([type, traits]) => (
                <div key={type} className="w-full">
                  <div
                    className="flex items-center gap-4 mb-4 p-3 bg-white/5 rounded-lg border border-white/10 cursor-pointer hover:bg-white/10 transition-colors"
                    onClick={() => toggleTraitType(type)}
                  >
                    <div className="w-1 h-6 bg-gradient-to-b from-brand-cyan to-brand-purple rounded-full" />
                    {expandedTypes[type] ? (
                      <ChevronDownIcon className="text-gray-400" />
                    ) : (
                      <ChevronRightIcon className="text-gray-400" />
                    )}
                    <h3 className="text-lg font-semibold text-white">
                      {type}
                    </h3>
                    <span className="text-sm text-gray-400 ml-auto">
                      {traits.length > 1 ? `${traits.length} traits` : `${traits.length} trait`}
                    </span>
                  </div>

                  {expandedTypes[type] && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 px-2 pt-2">
                        {traits.map((trait, index) => (
                          <TraitCard
                            key={trait.id}
                            trait={trait}
                            index={index}
                            selectedBooby={selectedBooby}
                            onSelectBooby={onSelectBooby}
                            onPurchase={handlePurchase}
                            coinRef={(el: HTMLDivElement | null) => (coinRefs.current[index] = el)}
                            AllFilteredNFTs={AllFilteredNFTs}
                            selectedCollection={selectedCollection!}
                            nftLoading={nftLoading}
                            getPrices={getPrices}
                            fetchWalletNfts={fetchWalletNfts}
                          />
                        ))}
                      </div>
                    </motion.div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-6 py-20 min-h-[400px]">
              <div className="w-32 h-32 text-brand-cyan/80 animate-float">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
              </div>

              <div className="flex flex-col gap-3 items-center text-center">
                <h3 className="text-xl font-bold text-white">
                  No Traits Found
                </h3>
                <p className="text-gray-400 max-w-md">
                  {selectedTraitType === "Select a Trait Type"
                    ? "Select a trait category to discover unique customization options"
                    : `We couldn't find any ${selectedTraitType.toLowerCase()} traits. Check back soon!`}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TraitSelectionPanel;
